"use client"

import {useCallback, useEffect, useRef, useState} from "react"
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card"
import { Button, Input, Tag } from "animal-island-ui"
import { AdminFilterSelect as Select } from "@/components/admin-filter-select"
import { CalendarBlank as Calendar, Funnel as Filter, List as Menu, Play, Plus, ArrowsClockwise as RefreshCw, MagnifyingGlass as Search, Trash, User, Lightning as Zap } from "@phosphor-icons/react"
import {apiRequestWithAuth, getStoredToken, isTokenValid} from "@/lib/api-config"
import {useToast} from "@/hooks/use-toast"
import {UserDetailDialog} from "@/components/user-detail-dialog"
import {UserEditDialog} from "@/components/user-edit-dialog"
import {UserAddDialog} from "@/components/user-add-dialog"
import {useAuth} from "@/contexts/auth-context"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import NotLoggedIn from "@/components/not-logged-in"
import {fetchMatchingUsers, userSearchParams, type UserCriteria} from "@/lib/user-selection"

interface UserAccount {
  id: number
  name: string
  account: string
  password: string
  freeze: number
  server: number
  taskType: string
  refresh: number
  agent: string | null
  createTime: string
  updateTime: string
  expireTime: string
  san: string
  config: any
  active: any
  notice: any
  delete: number
}

interface NewUserAccount {
  name: string
  account: string
  password?: string
  freeze: number
  server: number
  taskType: string
  refresh: number
  agent: string | null
  expireTime: string
  config: any
  active: any
  notice: any
}

interface UserListResponse {
  code: number
  msg: string
  data: {
    current: number
    page: number
    total: number
    records: UserAccount[]
  }
}

type BatchAction = "start" | "freeze" | "delete"
type BatchAccount = {readonly id: number; readonly name: string; readonly account: string}

export default function UsersPage() {
  const { token: contextToken } = useAuth()
  const { toast } = useToast()
  const [users, setUsers] = useState<UserAccount[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    size: 5,
    total: 0,
    page: 0,
  })

  const [searchForm, setSearchFormState] = useState({
    keyword: "",
    taskType: "",
    freeze: "",
    expired: "",
    deleted: "",
  })

  const [selectedUser, setSelectedUser] = useState<UserAccount | null>(null)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDetailDialog, setShowDetailDialog] = useState(false)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [batchBusy, setBatchBusy] = useState(false)
  const [selectAllBusy, setSelectAllBusy] = useState(false)
  const [batchConfirm, setBatchConfirm] = useState<null | "start" | "freeze" | "delete">(null)
  const [totalSelectable, setTotalSelectable] = useState(0)
  const [selectedAccounts, setSelectedAccounts] = useState<Map<number, {id: number; name: string; account: string}>>(new Map())
  const criteriaVersion = useRef(0)
  const criteriaRef = useRef<UserCriteria>(searchForm)
  const selectionRequest = useRef<AbortController | null>(null)
  const listRequest = useRef<AbortController | null>(null)
  const batchLock = useRef(false)
  const batchSnapshot = useRef<{readonly action: BatchAction; readonly accounts: readonly BatchAccount[]} | null>(null)

  const setSearchForm = (criteria: UserCriteria) => {
    if (batchLock.current || batchConfirm) return
    const unchanged = userSearchParams(criteria, 1, 5).toString() === userSearchParams(criteriaRef.current, 1, 5).toString()
    criteriaRef.current = criteria
    if (unchanged) {
      setSearchFormState(criteria)
      return
    }
    criteriaVersion.current++
    selectionRequest.current?.abort()
    selectionRequest.current = null
    listRequest.current?.abort()
    setSelectAllBusy(false)
    setSelectedIds(new Set())
    setSelectedAccounts(new Map())
    setTotalSelectable(0)
    setUsers([])
    setPagination(prev => ({ ...prev, current: 1 }))
    setSearchFormState(criteria)
  }

  const getToken = () => {
    return contextToken || getStoredToken()
  }

  const fetchUsers = useCallback(
    async (isSearchParam: boolean, pageToFetch: number, criteria = searchForm) => {
      // Mutation callbacks may hold an older render's criteria. Never let those
      // abort the current request or relabel old results as the current filter.
      if (userSearchParams(criteria, 1, 5).toString() !== userSearchParams(criteriaRef.current, 1, 5).toString()) return
      const token = getToken()
      if (!token || !isTokenValid(token)) {
        toast({
          variant: "destructive",
          title: "认证失败",
          description: "请重新登录",
        })
        return
      }

      setLoading(true)
      listRequest.current?.abort()
      const controller = new AbortController()
      listRequest.current = controller
      const version = criteriaVersion.current
      try {
        const params = userSearchParams(criteria, pageToFetch, pagination.size)

        const result: UserListResponse = await apiRequestWithAuth(`/searchAccount?${params.toString()}`, token, {
          method: "GET",
          signal: controller.signal,
        })
        if (controller.signal.aborted || version !== criteriaVersion.current) return
        if (result.code === 200) {
          setUsers(result.data.records)
          const removed = new Set(result.data.records.filter(user => user.delete === 1).map(user => user.id))
          setSelectedIds(prev => new Set([...prev].filter(id => !removed.has(id))))
          setSelectedAccounts(prev => {
            const next = new Map(prev)
            for (const {id, name, account, delete: deleted} of result.data.records) {
              if (deleted === 1) next.delete(id)
              else if (next.has(id)) next.set(id, {id, name, account})
            }
            return next
          })
          setPagination({
            current: result.data.current,
            size: pagination.size,
            total: result.data.total,
            page: result.data.page,
          })
        } else {
          toast({
            variant: "destructive",
            title: "获取用户列表失败",
            description: result.msg || "无法获取用户数据",
          })
        }
      } catch (error) {
        if (controller.signal.aborted || version !== criteriaVersion.current) return
        toast({
          variant: "destructive",
          title: "网络错误",
          description: error instanceof Error ? error.message : "无法连接到服务器",
        })
      } finally {
        if (listRequest.current === controller) setLoading(false)
      }
    },
    [
      contextToken,
      pagination.size,
      searchForm.keyword,
      searchForm.taskType,
      searchForm.freeze,
      searchForm.expired,
      searchForm.deleted,
      toast,
    ],
  )

  useEffect(() => {
    const token = getToken()
    if (token && isTokenValid(token)) {
      fetchUsers(false, 1).then(() => {
        // dev: ?dev_edit=1 自动打开第一个用户的编辑弹窗
        try {
          const qs = new URLSearchParams(window.location.search)
          if (process.env.NODE_ENV === "development" && qs.get("dev_edit") === "1") {
            // 用 api 直接拉一次，避免依赖 users 闭包
            apiRequestWithAuth("/showAccount?current=1&size=10", token, { method: "GET" })
              .then((res: any) => {
                if (res.code === 200 && res.data?.records?.length > 0) {
                  const u = res.data.records[0]
                  handleEdit(u)
                }
              })
              .catch(() => { /* ignore */ })
          }
        } catch (e) { /* ignore */ }
      })
    }
  }, [contextToken, fetchUsers])

  // Invalidate pending responses when leaving the page or changing auth session.
  useEffect(() => {
    setSelectedIds(new Set())
    setSelectedAccounts(new Map())
    setTotalSelectable(0)
    return () => {
      criteriaVersion.current++
      listRequest.current?.abort()
      selectionRequest.current?.abort()
    }
  }, [contextToken])

  const handleSearch = () => {
    if (batchLock.current || batchConfirm) return
    setPagination({ ...pagination, current: 1 })
    fetchUsers(true, 1)
  }

  const handleReset = () => {
    setSearchForm({
      keyword: "",
      taskType: "",
      freeze: "",
      expired: "",
      deleted: "",
    })
  }

  const handlePageChange = (newPage: number) => {
    if (batchLock.current || batchConfirm || loading) return
    setPagination({ ...pagination, current: newPage })
    fetchUsers(!!searchForm.keyword.trim(), newPage)
  }

  // 多选（应用当前筛选条件全选所有匹配页）
  const allSelected = totalSelectable > 0 && selectedIds.size === totalSelectable
  const someSelected = selectedIds.size > 0
  const pageSelectable = users.filter(user => user.delete !== 1)
  const pageSelected = pageSelectable.length > 0 && pageSelectable.every(user => selectedIds.has(user.id))

  const toggleSelect = (id: number) => {
    if (batchLock.current || batchConfirm || loading || selectAllBusy) return
    const account = users.find(user => user.id === id && user.delete !== 1)
    if (!account) return
    setSelectedAccounts(prev => new Map(prev).set(id, {id, name: account.name, account: account.account}))
    setTotalSelectable(0)
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = async () => {
    if (selectAllBusy || loading || batchLock.current || batchConfirm) return
    if (allSelected) {
      setSelectedIds(new Set())
      setTotalSelectable(0)
      return
    }
    const token = getToken()
    if (!token) return
    selectionRequest.current?.abort()
    const controller = new AbortController()
    selectionRequest.current = controller
    const version = criteriaVersion.current
    setSelectAllBusy(true)
    try {
      const records = await fetchMatchingUsers<UserAccount>(
        {...searchForm},
        url => apiRequestWithAuth<UserListResponse["data"]>(url, token, { method: "GET", signal: controller.signal }),
        controller.signal,
      )
      if (controller.signal.aborted || version !== criteriaVersion.current) return
      setSelectedIds(new Set(records.map(user => user.id)))
      setSelectedAccounts(new Map(records.map(user => [user.id, {id: user.id, name: user.name, account: user.account}])))
      setTotalSelectable(records.length)
      toast({ title: "已全选匹配用户", description: `应用当前筛选，共选中 ${records.length} 个未删除账号` })
    } catch (error) {
      if (!controller.signal.aborted && version === criteriaVersion.current) {
        toast({ variant: "destructive", title: "全选失败", description: error instanceof Error ? error.message : "请重试" })
      }
    } finally {
      if (selectionRequest.current === controller) {
        selectionRequest.current = null
        setSelectAllBusy(false)
      }
    }
  }

  const prepareBatch = (action: BatchAction) => {
    if (batchLock.current || loading || selectAllBusy || selectedIds.size === 0) return
    const accounts = [...selectedIds].map(id => selectedAccounts.get(id))
    if (accounts.some(account => !account)) {
      toast({variant: "destructive", title: "请重新选择用户", description: "所选账号信息不完整，未执行操作"})
      return
    }
    batchSnapshot.current = Object.freeze({
      action,
      accounts: Object.freeze(accounts.map(account => Object.freeze({...account!}))),
    })
    setBatchConfirm(action)
  }

  const runBatch = async (action: BatchAction) => {
    const snapshot = batchSnapshot.current
    if (batchLock.current || !snapshot || snapshot.action !== action || !snapshot.accounts.length) return
    const token = getToken()
    if (!token) return
    batchLock.current = true
    setBatchBusy(true)
    const failed = new Set<number>()
    const endpoint = action === "start" ? "/startAccountByAdmin" : action === "freeze" ? "/updateAccount" : "/delAccount"
    // 记下发起时的筛选/会话版本。离开页面或切换账号时清理逻辑会自增它，
    // 用来判断"这次批处理是否已经失效"。
    const version = criteriaVersion.current
    try {
      // Small batches limit load. No automatic retry of non-idempotent actions.
      for (let offset = 0; offset < snapshot.accounts.length; offset += 4) {
        // 已失效就不再下发剩余分片，避免用旧会话的 token 继续写数据
        if (version !== criteriaVersion.current) return
        await Promise.all(snapshot.accounts.slice(offset, offset + 4).map(async ({id}) => {
          try {
            const result = await apiRequestWithAuth(endpoint, token, {
              method: "POST",
              body: JSON.stringify(action === "freeze" ? {id, freeze: 1} : {id}),
            })
            if (result.code !== 200) failed.add(id)
          } catch {
            failed.add(id)
          }
        }))
      }
      // 回写前再确认一次，避免把陈旧结果写进已失效的选择状态、并触发无谓的列表刷新
      if (version !== criteriaVersion.current) return
      const okCount = snapshot.accounts.length - failed.size
      toast({
        variant: okCount > 0 ? "success" : "destructive",
        title: `批量${action === "start" ? "上号" : action === "freeze" ? "冻结" : "删除"}完成`,
        description: `成功 ${okCount}，失败或结果未确认 ${failed.size}${failed.size ? "；已保留未确认项，请先核实状态再重试" : ""}`,
      })
      setSelectedIds(failed)
      setSelectedAccounts(new Map(snapshot.accounts.filter(account => failed.has(account.id)).map(account => [account.id, account])))
      setTotalSelectable(0)
      await fetchUsers(false, action === "delete" ? 1 : pagination.current)
    } finally {
      batchSnapshot.current = null
      setBatchConfirm(null)
      batchLock.current = false
      setBatchBusy(false)
    }
  }

  const handleBatchStart = () => runBatch("start")
  const handleBatchFreeze = () => runBatch("freeze")
  const handleBatchDelete = () => runBatch("delete")

  const selectPage = (invert = false) => {
    if (batchLock.current || batchConfirm || loading || selectAllBusy) return
    const selectable = users.filter(user => user.delete !== 1)
    setSelectedAccounts(prev => {
      const next = new Map(prev)
      selectable.forEach(({id, name, account}) => next.set(id, {id, name, account}))
      return next
    })
    setSelectedIds(prev => {
      const next = new Set(prev)
      selectable.forEach(user => invert && next.has(user.id) ? next.delete(user.id) : next.add(user.id))
      return next
    })
    setTotalSelectable(0)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("zh-CN")
  }

  const isExpired = (expireTime: string) => {
    if (!expireTime) return false;
    return new Date(expireTime).getTime() < Date.now();
  }

  const getTaskTypeName = (taskType: string) => {
    const taskTypes: Record<string, string> = {
      daily: "日常任务",
      luxury: "豪华套餐",
      rogue: "肉鸽·傀影与猩红孤钻",
      rogue2: "肉鸽·水月与深蓝之树",
      sami: "肉鸽·探索者的银凇止境",
      sarkaz: "肉鸽·萨卡兹的无终奇语",
      sui: "肉鸽·界园",
      blackflow: "肉鸽·黑流树海·寻森小队",
      sand_fire: "生息演算·沙洲遗闻",
      icebreaker: "生息演算·重启锚点",
      goods: "商品任务",
    }
    return taskTypes[taskType] || taskType
  }

  const handleEdit = (user: UserAccount) => {
    setSelectedUser(user)
    setShowEditDialog(true)
  }

  const handleDetail = (user: UserAccount) => {
    setSelectedUser(user)
    setShowDetailDialog(true)
  }

  const handleSaveEdit = async (updatedData: Partial<UserAccount>): Promise<boolean> => {
    if (!selectedUser) return false

    const token = getToken()
    if (!token || !isTokenValid(token)) {
      toast({
        variant: "destructive",
        title: "认证失败",
        description: "请重新登录",
      })
      return false
    }

    setLoading(true)
    try {
      const payload = {
        id: selectedUser.id,
        ...updatedData,
        config: updatedData.config || {},
        active: updatedData.active || {},
        notice: updatedData.notice || {},
      } as UserAccount

      // 密码留空则不修改，后端保留原密码（password 从提交载荷中移除）
      if (!payload.password?.trim()) {
        const clean = payload as { password?: string }
        delete clean.password
      }

      const result = await apiRequestWithAuth("/updateAccount", token, {
        method: "POST",
        body: JSON.stringify(payload),
      })

      if (result.code === 200) {
        toast({
          variant: "success",
          title: "保存成功",
          description: "用户信息已更新，已清空旧选择",
        })
        setSelectedIds(new Set())
        setSelectedAccounts(new Map())
        setTotalSelectable(0)
        await fetchUsers(!!searchForm.keyword.trim(), pagination.current)
        return true
      } else {
        toast({
          variant: "destructive",
          title: "保存失败",
          description: result.msg || "更新用户信息时发生错误",
        })
        return false
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "网络错误",
        description: error instanceof Error ? error.message : "无法连接到服务器",
      })
      return false
    } finally {
      setLoading(false)
    }
  }

  const handleStartAccount = async (id: number) => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return

    setLoading(true)
    try {
      const result = await apiRequestWithAuth("/startAccountByAdmin", token, {
        method: "POST",
        body: JSON.stringify({ id }),
      })
      if (result.code === 200) {
        toast({
          variant: "success",
          title: "操作成功",
          description: `用户 ${id} 已立即上号`,
        })
        await fetchUsers(!!searchForm.keyword.trim(), pagination.current)
      } else {
        toast({
          variant: "destructive",
          title: "操作失败",
          description: result.msg || `无法立即上号用户 ${id}`,
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "网络错误",
        description: error instanceof Error ? error.message : "操作失败",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleResetSanity = async (id: number) => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return

    setLoading(true)
    try {
      const result = await apiRequestWithAuth("/resetAccountDynamicInfo", token, {
        method: "POST",
        body: JSON.stringify({ id }),
      })
      if (result.code === 200) {
        toast({
          variant: "success",
          title: "操作成功",
          description: `用户 ${id} 理智信息已重置`,
        })
        await fetchUsers(!!searchForm.keyword.trim(), pagination.current)
      } else {
        toast({
          variant: "destructive",
          title: "操作失败",
          description: result.msg || `无法重置用户 ${id} 理智信息`,
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "网络错误",
        description: error instanceof Error ? error.message : "操作失败",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleResetRefresh = async (id: number) => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return

    setLoading(true)
    try {
      const result = await apiRequestWithAuth("/resetRefresh", token, {
        method: "POST",
        body: JSON.stringify({ id }),
      })
      if (result.code === 200) {
        toast({
          variant: "success",
          title: "操作成功",
          description: `用户 ${id} 刷新次数已重置`,
        })
        await fetchUsers(!!searchForm.keyword.trim(), pagination.current)
      } else {
        toast({
          variant: "destructive",
          title: "操作失败",
          description: result.msg || `无法重置用户 ${id} 刷新次数`,
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "网络错误",
        description: error instanceof Error ? error.message : "操作失败",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteAccount = async (id: number) => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return

    setLoading(true)
    try {
      const result = await apiRequestWithAuth("/delAccount", token, {
        method: "POST",
        body: JSON.stringify({ id }),
      })
      if (result.code === 200) {
        toast({
          variant: "success",
          title: "删除成功",
          description: `用户 ${id} 已被删除`,
        })
        setSelectedIds(prev => { const next = new Set(prev); next.delete(id); return next })
        setSelectedAccounts(prev => { const next = new Map(prev); next.delete(id); return next })
        setTotalSelectable(0)
        await fetchUsers(false, 1)
      } else {
        toast({
          variant: "destructive",
          title: "删除失败",
          description: result.msg || `无法删除用户 ${id}`,
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "网络错误",
        description: error instanceof Error ? error.message : "操作失败",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleAddNewUser = async (newUserData: Partial<NewUserAccount>): Promise<boolean> => {
    const token = getToken()
    if (!token || !isTokenValid(token)) {
      toast({
        variant: "destructive",
        title: "认证失败",
        description: "请重新登录",
      })
      return false
    }

    setLoading(true)
    try {
      const result = await apiRequestWithAuth("/addAccount", token, {
        method: "POST",
        body: JSON.stringify(newUserData),
      })

      if (result.code === 200) {
        toast({
          variant: "success",
          title: "添加成功",
          description: "新用户已成功添加",
        })
        setPagination((prev) => ({ ...prev, current: 1 }))
        await fetchUsers(false, 1)
        return true
      } else {
        toast({
          variant: "destructive",
          title: "添加失败",
          description: result.msg || "添加新用户时发生错误",
        })
        return false
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "网络错误",
        description: error instanceof Error ? error.message : "无法连接到服务器",
      })
      return false
    } finally {
      setLoading(false)
    }
  }

  const token = getToken()
  if (!token || !isTokenValid(token)) {
    return (
      <NotLoggedIn />
    )
  }

  return (
    <>
      <div className="mb-6">
        <div className="page-title-box">
          <div className="page-title">用户管理</div>
          <div className="page-subtitle">管理系统中的所有用户账号</div>
        </div>
      </div>

      <Card className="mb-6" style={{ border: "1.5px solid var(--leaf-500)", background: "radial-gradient(circle, rgba(138,198,138,0.18) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(180,220,180,0.12) 1px, transparent 1px), var(--leaf-50)", color: "var(--leaf-900)" }}>
        <CardContent className="admin-search-panel">
          <form className="admin-search-row" onSubmit={(event) => { event.preventDefault(); handleSearch() }}>
            <div>
              <label htmlFor="keyword" className="sr-only">用户名或账号</label>
              <Input id="keyword" placeholder="搜索用户名或账号" value={searchForm.keyword} onChange={(e) => setSearchForm({ ...searchForm, keyword: e.target.value })} size="small" />
            </div>
            <Button htmlType="submit" disabled={loading} icon={<Search size={16} />}>搜索</Button>
          </form>
          <div className="admin-filter-selection-group">
          <details className="admin-search-filters">
            <summary><Filter size={16} /><span>筛选条件</span><span className="admin-filter-count">{[searchForm.taskType,searchForm.freeze,searchForm.expired,searchForm.deleted].filter(value => value && value !== "all").length || "不限"}</span><Menu size={18} className="admin-filter-toggle-icon" aria-hidden="true" /></summary>
          <section className="admin-tool-module" aria-label="筛选条件">
          <div className="admin-filter-fields">
            <div className="space-y-2">
              <label style={{ fontSize: 14, fontWeight: 600, color: "var(--leaf-900)", marginBottom: 12, display: "block" }}>任务类型</label>
              <Select
                options={[
                  { key: "all", label: "全部" },
                  { key: "daily", label: "日常任务" },
                  { key: "luxury", label: "豪华套餐" },
                  { key: "rogue", label: "肉鸽·傀影" },
                  { key: "rogue2", label: "肉鸽·水月" },
                  { key: "sami", label: "肉鸽·银凇止境" },
                  { key: "sarkaz", label: "肉鸽·萨卡兹" },
                  { key: "sui", label: "肉鸽·界园" },
                  { key: "blackflow", label: "肉鸽·黑流树海" },
                  { key: "sand_fire", label: "生息演算·沙洲遗闻" },
                  { key: "icebreaker", label: "生息演算·重启锚点" },
                ]}
                value={searchForm.taskType}
                onChange={(value) => setSearchForm({ ...searchForm, taskType: value })}
                placeholder="选择任务类型"
                size="small"
              />
            </div>

            <div className="space-y-2">
              <label style={{ fontSize: 14, fontWeight: 600, color: "var(--leaf-900)", marginBottom: 12, display: "block" }}>是否冻结</label>
              <Select
                options={[
                  { key: "all", label: "全部" },
                  { key: "true", label: "是" },
                  { key: "false", label: "否" },
                ]}
                value={searchForm.freeze}
                onChange={(value) => setSearchForm({ ...searchForm, freeze: value })}
                placeholder="选择冻结状态"
                size="small"
              />
            </div>

            <div className="space-y-2">
              <label style={{ fontSize: 14, fontWeight: 600, color: "var(--leaf-900)", marginBottom: 12, display: "block" }}>是否到期</label>
              <Select
                options={[
                  { key: "all", label: "全部" },
                  { key: "true", label: "是" },
                  { key: "false", label: "否" },
                ]}
                value={searchForm.expired}
                onChange={(value) => setSearchForm({ ...searchForm, expired: value })}
                placeholder="选择到期状态"
                size="small"
              />
            </div>
            <div className="space-y-2">
              <label style={{ fontSize: 14, fontWeight: 600, color: "var(--leaf-900)", marginBottom: 12, display: "block" }}>是否已删除</label>
              <Select
                options={[
                  { key: "all", label: "全部" },
                  { key: "true", label: "是" },
                  { key: "false", label: "否" },
                ]}
                value={searchForm.deleted}
                onChange={(value) => setSearchForm({ ...searchForm, deleted: value })}
                placeholder="选择删除状态"
                size="small"
              />
            </div>
          </div>

          <div className="admin-filter-actions">
            <Button type="default" onClick={handleReset} disabled={loading}>清空条件</Button>
            <Button type="default" onClick={handleSearch} disabled={loading}>应用筛选</Button>
          </div>
          </section>
          <section className="admin-tool-module" aria-label="多选操作">
            <h3>多选操作 <span>已选 {selectedIds.size} 个</span></h3>
            <div className="admin-selection-actions">
                  <button type="button" disabled={loading || batchBusy || selectAllBusy} onClick={() => selectPage()}>选择本页结果</button>
                  <button type="button" disabled={loading || batchBusy || selectAllBusy} onClick={() => selectPage(true)}>反选本页结果</button>
                  <button type="button" disabled={!someSelected || loading || batchBusy || selectAllBusy} onClick={() => { setSelectedIds(new Set()); setTotalSelectable(0) }}>取消所有选择</button>
                  <button type="button" disabled={!someSelected || loading || batchBusy || selectAllBusy} onClick={() => prepareBatch("start")}>批量上号</button>
                  <button type="button" disabled={!someSelected || loading || batchBusy || selectAllBusy} onClick={() => prepareBatch("freeze")}>批量冻结</button>
                  <button type="button" className="admin-multi-danger" disabled={!someSelected || loading || batchBusy || selectAllBusy} onClick={() => prepareBatch("delete")}>批量删除</button>
            </div>
          </section>
          </details>
          </div>
        </CardContent>
      </Card>
      <Card style={{ border: "1.5px solid var(--leaf-500)", background: "radial-gradient(circle, rgba(138,198,138,0.18) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(180,220,180,0.12) 1px, transparent 1px), var(--leaf-50)", color: "var(--leaf-900)" }}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2 flex-wrap text-leaf-900">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                aria-label="选择本页用户"
                checked={pageSelected}
                disabled={loading || batchBusy || selectAllBusy || pageSelectable.length === 0}
                ref={(el) => {
                  if (el) el.indeterminate = !pageSelected && pageSelectable.some(user => selectedIds.has(user.id))
                }}
                onChange={() => selectPage(pageSelected)}
                style={{ cursor: "pointer", width: 16, height: 16 }}
              />
              <span>用户列表</span>
              <Tag color="lime-green" size="small">共 {pagination.total} 个用户</Tag>
              {someSelected && <Tag color="app-orange" size="small">已选 {selectedIds.size}</Tag>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="small" type="default" disabled={loading || batchBusy || selectAllBusy} onClick={toggleSelectAll}>
                {selectAllBusy ? "正在选择…" : allSelected ? "取消全选匹配用户" : "全选匹配用户"}
              </Button>
              <Button type="default" icon={<Plus style={{ width: 16, height: 16 }} />} onClick={() => setShowAddDialog(true)} disabled={loading}>增加用户</Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin mr-2" />
              <span className="text-leaf-900">加载中...</span>
            </div>
          ) : users.length > 0 ? (
            <div className="space-y-4">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="admin-user-card"
                  style={{ border: "1.5px solid var(--leaf-500)", borderRadius: 12, padding: 16, background: "var(--leaf-100)", opacity: user.delete === 1 ? 0.6 : 1 }}
                >
                  <div className="admin-user-card__body flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="admin-user-card__identity min-w-0 flex-1">
                      <div className="admin-user-card__header flex flex-wrap items-center gap-2 mb-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(user.id)}
                          onChange={() => toggleSelect(user.id)}
                          aria-label={`选择用户 ${user.name || user.account}`}
                          disabled={user.delete === 1 || batchBusy || selectAllBusy}
                          style={{ cursor: user.delete === 1 ? "not-allowed" : "pointer", width: 16, height: 16 }}
                        />
                        <User className="h-5 w-5 text-gray-500" />
                        <h3 className="min-w-0 break-words font-semibold text-lg text-leaf-900">{user.name}</h3>
                        <div className="admin-user-card__status flex flex-wrap gap-2">
                          <span className={`admin-status-pill ${user.freeze ? "admin-status-pill--frozen" : "admin-status-pill--ok"}`}>{user.freeze ? "已冻结" : "正常"}</span>
                          {user.delete === 1 ? (
                            <span className="admin-status-pill admin-status-pill--deleted">已删除</span>
                          ) : isExpired(user.expireTime) ? (
                            <span className="admin-status-pill admin-status-pill--expired">已到期</span>
                          ) : (
                            <span className="admin-status-pill admin-status-pill--ok">有效</span>
                          )}
                        </div>
                      </div>

                      <div className="admin-user-card__mobile-info">
                        <div>{user.account} · {user.server === 0 ? "官服" : "B服"} · {getTaskTypeName(user.taskType)}</div>
                        <div>理智 {user.san || "—"} · 到期 {formatDate(user.expireTime).split(" ")[0]}</div>
                      </div>
                      <div className="admin-user-card__details grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500 text-leaf-700">账号: </span>
                          <span className="text-leaf-900">{user.account}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 text-leaf-700">任务类型: </span>
                          <span className="text-leaf-900">{getTaskTypeName(user.taskType)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 text-leaf-700">服务器: </span>
                          <span className="text-leaf-900">{user.server === 0 ? "官服" : "B服"}</span>{" "}
                        </div>
                        <div className="flex items-center gap-1">
                          <Zap className="h-4 w-4 text-blue-500" />
                          <span className="text-gray-500 text-leaf-700">理智: </span>
                          <span className="text-leaf-900">{user.san}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <RefreshCw className="h-4 w-4 text-green-500" />
                          <span className="text-gray-500 text-leaf-700">刷新次数: </span>
                          <span className="text-leaf-900">{user.refresh}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-orange-500" />
                          <span className="text-gray-500 text-leaf-700">到期时间: </span>
                          <span className="text-leaf-900">{formatDate(user.expireTime)}</span>
                        </div>
                      </div>

                      {user.agent && (
                        <div className="admin-user-card__agent mt-2">
                          <span className="text-gray-500 text-leaf-700">代理: </span>
                          <span className="text-leaf-900">{user.agent}</span>
                        </div>
                      )}
                    </div>

                    <div className="admin-user-card__actions grid w-full grid-cols-2 gap-2 sm:grid-cols-3 md:mt-0 md:w-auto">
                      <Button
                        size="small"
                        className="whitespace-nowrap"
                        onClick={() => handleEdit(user)}
                        disabled={loading}
                        
                      >
                        编辑
                      </Button>
                      <Button size="small" className="whitespace-nowrap" onClick={() => handleStartAccount(user.id)} disabled={loading}>
                        
                        立即上号
                      </Button>
                      <details className="admin-user-card__more">
                        <summary>更多</summary>
                        <div className="admin-user-card__more-panel">
                      <Button
                        size="small"
                        className="whitespace-nowrap"
                        type="default"
                        onClick={() => window.location.href = `/admin/logs?account=${encodeURIComponent(user.account)}`}
                        disabled={loading}
                      >
                        查询日志
                      </Button>
                      <Button size="small" className="whitespace-nowrap" type="default" onClick={() => handleDetail(user)} disabled={loading}>
                        详情
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="small" className="whitespace-nowrap" disabled={loading} style={{
                            background: "rgba(193,69,69,0.15)",
                            borderColor: "var(--berry-500)",
                            color: "var(--berry-500)",
                          }}>
                            
                            删除
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent style={{ background: "var(--cream-50)" }}>
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-leaf-900">确认删除用户？</AlertDialogTitle>
                            <AlertDialogDescription className="text-leaf-700">
                              此操作将永久删除用户 {user.name} 的所有数据。此操作不可撤销。
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="dark:border-gray-600 text-leaf-900">取消</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteAccount(user.id)}
                              
                            >
                              删除
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                        </div>
                      </details>
                    </div>
                  </div>
                </div>
              ))}

              <div className="responsive-pagination flex items-center justify-between pt-4">
                <div className="text-sm text-gray-500 text-leaf-700">
                  显示第 {(pagination.current - 1) * pagination.size + 1} -{" "}
                  {Math.min(pagination.current * pagination.size, pagination.total)} 条，共 {pagination.total} 条
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="small"
                    type="default"
                    disabled={pagination.current <= 1}
                    onClick={() => handlePageChange(pagination.current - 1)}
                  >
                    上一页
                  </Button>
                  <span aria-live="polite">第 {pagination.current} / {pagination.page} 页</span>
                  <Button
                    size="small"
                    type="default"
                    disabled={pagination.current >= pagination.page}
                    onClick={() => handlePageChange(pagination.current + 1)}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-leaf-700">暂无用户数据</p>
            </div>
          )}
        </CardContent>
      </Card>
      <UserDetailDialog user={selectedUser} open={showDetailDialog} onOpenChange={setShowDetailDialog}
        onResetRefresh={handleResetRefresh}
        onResetSanity={handleResetSanity}
      />
      <UserEditDialog
        user={selectedUser}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onSave={handleSaveEdit}
      />
      <UserAddDialog open={showAddDialog} onOpenChange={setShowAddDialog} onSave={handleAddNewUser} />
      <AlertDialog
        open={batchConfirm !== null}
        onOpenChange={(open) => {
          if (!open && !batchLock.current) {
            batchSnapshot.current = null
            setBatchConfirm(null)
          }
        }}
      >
        <AlertDialogContent style={{ background: "var(--cream-50)" }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-leaf-900">
              {batchConfirm === "start" && `确认立即上号选中的 ${batchSnapshot.current?.accounts.length || 0} 个账号？`}
              {batchConfirm === "freeze" && `确认冻结选中的 ${batchSnapshot.current?.accounts.length || 0} 个账号？`}
              {batchConfirm === "delete" && `确认删除选中的 ${batchSnapshot.current?.accounts.length || 0} 个账号？`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-leaf-700">
              {batchConfirm === "start" && "将按当前任务配置依次登录所选账号。"}
              {batchConfirm === "freeze" && "冻结后账号将暂停执行任务，可随时解冻。"}
              {batchConfirm === "delete" && "此操作不可恢复！"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-48 overflow-y-auto rounded-xl border p-3 text-sm" aria-label="本次操作账号清单">
            {batchSnapshot.current?.accounts.map(account => <div key={account.id} className="break-words py-1">{account.name || "未命名"} · {account.account}（ID {account.id}）</div>)}
          </div>
          <p className="text-sm text-leaf-700" aria-live="polite">{batchBusy ? "正在执行，请勿关闭或刷新页面…" : "仅操作以上确认清单；已删除用户不会被选中。"}</p>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={batchBusy} className="dark:border-gray-600 text-leaf-900">取消</AlertDialogCancel>
            <AlertDialogAction
              className={batchConfirm === "delete" ? "bg-red-600 hover:bg-red-700 text-white" : undefined}
              disabled={batchBusy}
              onClick={(event) => {
                event.preventDefault()
                const action = batchConfirm
                if (action === "start") handleBatchStart()
                else if (action === "freeze") handleBatchFreeze()
                else if (action === "delete") handleBatchDelete()
              }}
            >
              {batchBusy ? "执行中…" : "确认"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
